import { colors } from '../../styles/theme';
import { formatDate, formatMoney } from '../../lib/datetime';
import { CALL_CONTEXT } from '../../lib/executiveRefusal';
import RequestCallButton from './RequestCallButton';
import type { RedemptionRequest } from '../../services/redemptions.service';

/** What an exit window's gate paid and what it deferred: the deferred part waits for the next window (S-47). */
export function deferredParts(requests: readonly RedemptionRequest[]): RedemptionRequest[] {
  return requests.filter(r => r.status === 'partially_filled' && r.amount_remaining != null);
}

/** Whole cents of a two-decimal money string, so the subtraction is exact. */
function cents(value: string): number {
  return Math.round(Number(value) * 100);
}

function paidSoFar(request: RedemptionRequest): string {
  const paid = cents(request.amount) - cents(request.amount_remaining ?? '0');
  return (paid / 100).toFixed(2);
}

/**
 * A partner's requests that an exit window paid only in part: what was paid, what waits and the date of the window it waits
 * for, with the call to their ejecutivo de cuenta (no dead ends: the next step is always on screen).
 */
export default function NavWindowDeferred({
  requests,
}: Readonly<{ requests: readonly RedemptionRequest[] }>): JSX.Element | null {
  const deferred = deferredParts(requests);
  if (deferred.length === 0) return null;
  return (
    <section className="mb-4">
      <h2 className="text-sm font-bold uppercase tracking-widest mb-3" style={{ color: colors.inkSoft }}>
        Waiting for the next window
      </h2>
      <ul className="space-y-2">
        {deferred.map(r => (
          <li key={r.id} className="rounded-xl p-4 text-sm space-y-1"
              style={{ background: colors.surface, border: `1px solid ${colors.border}`, color: colors.ink }}>
            <div className="font-medium">{r.product_name}</div>
            <p className="m-0">
              {`${formatMoney(paidSoFar(r), r.currency_code)} of ${formatMoney(r.amount, r.currency_code)} was paid at the window.`}
            </p>
            <p className="m-0" style={{ color: colors.inkSoft }}>
              {`The rest, ${formatMoney(r.amount_remaining ?? '0', r.currency_code)}, is paid at the window of ${formatDate(r.eligible_at)}, at that window's approved NAV.`}
            </p>
            <RequestCallButton productId={r.product_id} context={CALL_CONTEXT.withdrawal} />
          </li>
        ))}
      </ul>
    </section>
  );
}
